import {
  makeStyles,
  makeResetStyles,
  mergeClasses,
  shorthands,
  typographyStyles,
  createFocusOutlineStyle,
} from '@fluentui/react-components';
import { tokens } from '@fluentui-copilot/tokens';
import type { PromptStarterSlots, PromptStarterState } from './PromptStarter.types';
import type { SlotClassNames } from '@fluentui/react-components';

export const promptStarterClassNames: SlotClassNames<PromptStarterSlots> = {
  root: 'fai-PromptStarter',
  primaryAction: 'fai-PromptStarter__primaryAction',
  icon: 'fai-PromptStarter__icon',
  category: 'fai-PromptStarter__category',
  prompt: 'fai-PromptStarter__prompt',
  reasonMarker: 'fai-PromptStarter__reasonMarker',
  actions: 'fai-PromptStarter__actions',
};

const useRootResetStyles = makeResetStyles({
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gridTemplateRows: 'max-content auto',
  minWidth: '214px',

  // Apply styles to the primary action on hover / active states
  '&:hover': {
    [`& .${promptStarterClassNames.primaryAction}`]: {
      boxShadow: tokens.shadow16,
      transform: 'scale(1.03)',
      transition: `transform ${tokens.durationNormal} ${tokens.curveDecelerateMin}`,
    },
  },
  '&:active': {
    [`& .${promptStarterClassNames.primaryAction}`]: {
      boxShadow: tokens.shadow8,
      transform: 'scale(1.03)',
      transition: `transform ${tokens.durationNormal} ${tokens.curveDecelerateMin}`,
    },
  },

  // Hide actions when not focused or hovered
  ':not(:focus-within):not(:hover)': {
    [`& .${promptStarterClassNames.actions}`]: {
      clip: 'rect(0px, 0px, 0px, 0px)',
      height: '1px',
      margin: '-1px',
      overflow: 'hidden',
      padding: '0px',
      width: '1px',
      position: 'absolute',
      opacity: 0,
    },
  },
});

const useStyles = makeStyles({
  primaryAction: {
    gridRow: '1 / 3',
    gridColumn: '1 / 3',
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    gridAutoRows: 'max-content 1fr max-content',
    borderRadius: '28px',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground2,
    fontFamily: tokens.fontFamilyBase,
    cursor: 'pointer',
    outlineStyle: 'none',
    textAlign: 'left',
    columnGap: tokens.spacingHorizontalMNudge,
    rowGap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    ...createFocusOutlineStyle({ style: { outlineRadius: '28px' } }),
  },

  primaryActionHovered: {
    boxShadow: tokens.shadow16,
    transform: 'scale(1.03)',
    transition: `transform ${tokens.durationNormal} ${tokens.curveDecelerateMin}`,
  },

  icon: {
    display: 'flex',
    alignItems: 'center',
    minHeight: tokens.spacingVerticalXXL,
    gridRow: 1,
    gridColumn: '1 / 2',
  },
  category: {
    gridRow: 1,
    gridColumn: '2 / 3',
    alignSelf: 'center',

    color: tokens.colorNeutralForeground1,
    ...typographyStyles.body1Strong,

    // Truncate text after one line.
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical',
    '-webkit-line-clamp': '1',
    overflowY: 'hidden',
    wordBreak: 'break-word',
  },
  actions: {
    gridRow: '1 / 2',
    gridColumn: '2 / 3',
    position: 'relative',
    margin: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}
             ${tokens.spacingVerticalNone} ${tokens.spacingHorizontalNone}`,
    opacity: 1,
    transition: `opacity ${tokens.durationFast} ${tokens.curveLinear}`,
  },
  actionsWithCategory: {
    // Add a background and gradient fade to truncate the title if applicable
    backgroundColor: tokens.colorNeutralBackground1,

    boxShadow: `-12px 0 12px 0 ${tokens.colorNeutralBackground1}`,
  },
  prompt: {
    gridRow: '2',
    gridColumn: '1 / 3',

    ...typographyStyles.body1Strong,

    // Truncate text after two lines.
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical',
    '-webkit-line-clamp': '2',
    overflowY: 'hidden',
    wordBreak: 'break-word',
  },
  promptNoReasonMarker: {
    // Truncate text after three lines when no reason marker is present.
    '-webkit-line-clamp': '3',
  },
  reasonMarker: {
    gridRow: '3',
    gridColumn: '1 / 3',

    ...typographyStyles.caption2,

    // Truncate text after one line.
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical',
    '-webkit-line-clamp': '1',
    overflowY: 'hidden',
    wordBreak: 'break-word',
  },
});

const useSingleColumnStyles = makeStyles({
  primaryAction: {
    display: 'flex',
    flexDirection: 'column',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadius2XL,
    ...createFocusOutlineStyle({ style: { outlineRadius: tokens.borderRadius2XL } }),
  },
  category: {
    ...typographyStyles.caption1Strong,
    alignSelf: 'auto',
  },
  prompt: {
    ...typographyStyles.caption1Strong,

    // Always truncate after two lines in single column layout.
    '-webkit-line-clamp': '2',
  },
});

export const usePromptStarterStyles_unstable = (state: PromptStarterState): PromptStarterState => {
  'use no memo';

  const { isSingleColumn } = state;

  const styles = useStyles();
  const rootResetStyles = useRootResetStyles();
  const singleColumnStyles = useSingleColumnStyles();
  state.root.className = mergeClasses(promptStarterClassNames.root, rootResetStyles, state.root.className);
  state.primaryAction.className = mergeClasses(
    promptStarterClassNames.primaryAction,
    styles.primaryAction,
    isSingleColumn && singleColumnStyles.primaryAction,
    state.primaryAction.className,
  );
  if (state.icon) {
    state.icon.className = mergeClasses(promptStarterClassNames.icon, styles.icon, state.icon.className);
  }
  if (state.category) {
    state.category.className = mergeClasses(
      promptStarterClassNames.category,
      styles.category,
      isSingleColumn && singleColumnStyles.category,
      state.category.className,
    );
  }
  state.prompt.className = mergeClasses(
    promptStarterClassNames.prompt,
    styles.prompt,
    !state.reasonMarker && styles.promptNoReasonMarker,
    isSingleColumn && singleColumnStyles.prompt,
    state.prompt.className,
  );
  if (state.reasonMarker) {
    state.reasonMarker.className = mergeClasses(
      promptStarterClassNames.reasonMarker,
      styles.reasonMarker,
      state.reasonMarker.className,
    );
  }
  if (state.actions) {
    state.actions.className = mergeClasses(
      promptStarterClassNames.actions,
      styles.actions,
      state.category && styles.actionsWithCategory,
      state.actions.className,
    );
  }

  return state;
};
